import React from 'react';
import { Link } from 'react-router-dom';
import './LegalPage.css';

const TermsOfServicePage = () => {
  return (
    <div className="legal-page">
      <div className="legal-container">

        <div className="legal-header">
          <h1>Terms of Service</h1>
          <p className="legal-meta">Last updated: March 21, 2026 &nbsp;·&nbsp; Effective date: March 21, 2026</p>
        </div>

        <div className="legal-body">

          <section>
            <h2>1. Acceptance of Terms</h2>
            <p>
              These Terms of Service ("Terms") constitute a legally binding agreement between you
              and MyDocAI ("we," "us," or "our") governing your access to and use of the MyDocAI
              web application, mobile application, API, and all associated services (collectively,
              the "Service"). Please read these Terms carefully before using the Service.
            </p>
            <p>
              By registering for an account, clicking "I agree," or otherwise accessing or using
              the Service, you represent that you have read, understood, and agree to be bound by
              these Terms and our Privacy Policy, which is incorporated herein by reference. If you
              do not agree to these Terms, you may not access or use the Service.
            </p>
            <p>
              If you are using the Service on behalf of an organization, you represent and warrant
              that you have the authority to bind that organization to these Terms, and "you" and
              "your" in these Terms refer to that organization.
            </p>
          </section>

          <section>
            <h2>2. Eligibility</h2>
            <p>
              You must be at least 18 years of age to use the Service. By using the Service, you
              represent and warrant that you are at least 18 years old, have the legal capacity to
              enter into this agreement, and are not prohibited from using the Service under
              applicable law. If you are using the Service on behalf of a business entity, you
              further represent that the entity is duly organized and in good standing under
              applicable law.
            </p>
            <p>
              MyDocAI reserves the right to refuse access to the Service to any person or entity
              at any time for any reason, including for violation of these Terms.
            </p>
          </section>

          <section>
            <h2>3. Description of the Service</h2>
            <p>
              MyDocAI is an AI-powered document intelligence platform that enables users to upload
              documents, extract and analyze their contents using artificial intelligence, ask
              natural language questions about document content, and receive AI-assisted support
              for form completion tasks. The Service is designed to streamline information
              retrieval from documents and reduce manual data entry.
            </p>
            <p>
              The Service is currently offered in a beta capacity as we continue active development.
              Features, functionality, pricing, and availability are subject to change. We will
              make reasonable efforts to communicate significant changes in advance but reserve
              the right to modify the Service at any time.
            </p>
            <p>
              The Service is intended for personal and professional productivity use. It is not
              intended to serve as a substitute for professional legal, medical, financial, or
              any other regulated advisory service. No output generated by the Service should be
              relied upon as professional advice.
            </p>
          </section>

          <section>
            <h2>4. Account Registration and Security</h2>
            <p>
              To access most features of the Service, you must create an account. When registering,
              you agree to provide accurate, current, and complete information and to keep this
              information updated. Providing false, inaccurate, or misleading information is
              grounds for immediate account termination.
            </p>
            <p>
              You are solely responsible for maintaining the confidentiality of your account
              credentials, including your password. You agree not to share your credentials with
              any third party and to take reasonable steps to prevent unauthorized access to your
              account. You are responsible for all activity that occurs under your account,
              whether or not authorized by you.
            </p>
            <p>
              You agree to notify us immediately at{' '}
              <a href="mailto:support@mydocai.org">support@mydocai.org</a> if you suspect or
              discover any unauthorized access to your account or any other security breach.
              We are not liable for any loss or damage resulting from unauthorized use of your
              account credentials.
            </p>
            <p>
              We reserve the right to require you to reset your password or take other security
              actions if we believe your account security has been compromised.
            </p>
          </section>

          <section>
            <h2>5. Acceptable Use Policy</h2>
            <p>
              You agree to use the Service only for lawful purposes and in a manner that does not
              infringe the rights of others or restrict or inhibit their use of the Service. The
              following activities are expressly prohibited:
            </p>
            <p>
              You may not upload, transmit, or process documents that contain content that is
              unlawful, harassing, defamatory, obscene, fraudulent, or that violates any
              third party's intellectual property or privacy rights. You may not upload documents
              containing malware, viruses, trojans, or any other malicious code.
            </p>
            <p>
              You may not use the Service to process documents belonging to a third party without
              that party's explicit consent, or in violation of any confidentiality obligation you
              owe to that party. This includes documents subject to attorney-client privilege,
              medical record privacy regulations (such as HIPAA), or financial data regulations,
              unless you have independently verified that your use is compliant with all applicable
              rules governing those document types.
            </p>
            <p>
              You may not attempt to circumvent, disable, or interfere with security features of
              the Service, including authentication systems, access controls, or encryption.
              You may not conduct penetration testing, vulnerability scanning, or any form of
              automated probing without express written authorization from MyDocAI.
            </p>
            <p>
              You may not use automated scripts, bots, crawlers, or other tools to access the
              Service at a rate that exceeds normal human usage, to bulk-extract data, or to
              reverse-engineer the Service's underlying functionality. You may not attempt to
              access other users' accounts, data, or any systems or networks connected to the
              Service without authorization.
            </p>
            <p>
              Violation of this Acceptable Use Policy may result in immediate suspension or
              termination of your account, and we reserve the right to report unlawful conduct to
              appropriate authorities.
            </p>
          </section>

          <section>
            <h2>6. User Content and License</h2>
            <p>
              "User Content" means all documents, files, text, data, and other content that you
              upload to or generate through the Service. You retain full ownership of all User
              Content you submit to the Service. These Terms do not transfer any ownership rights
              in your User Content to MyDocAI.
            </p>
            <p>
              By uploading User Content, you grant MyDocAI a limited, non-exclusive, royalty-free
              license to access, process, store, analyze, and display your User Content solely to
              the extent necessary to provide the Service to you. This license is limited to the
              technical operations required to deliver the features you request and terminates when
              you delete the content or close your account.
            </p>
            <p>
              You represent and warrant that you own your User Content or have all necessary rights
              and permissions to upload it and grant us the license described above, and that your
              User Content does not violate any law or infringe any third-party rights. You are
              solely responsible for the accuracy, legality, and appropriateness of your User Content.
            </p>
            <p>
              We do not claim any rights to your User Content beyond those necessary to operate the
              Service. We will never use your documents to train AI models, build derivative datasets,
              or provide your content to other users or third parties except as described in our
              Privacy Policy.
            </p>
          </section>

          <section>
            <h2>7. AI-Generated Output and Disclaimer of Reliance</h2>
            <p>
              The Service uses large language models and other AI technologies to analyze your
              documents and generate responses. AI-generated output is probabilistic in nature and
              may contain errors, omissions, hallucinations, or inaccuracies. The accuracy of
              AI-generated output depends heavily on the quality, completeness, and clarity of the
              documents you upload.
            </p>
            <p>
              You acknowledge and agree that AI-generated output from the Service does not constitute
              legal advice, medical advice, financial advice, tax advice, or any other form of
              professional advisory service. You should not rely on AI-generated output for
              important decisions without independent verification from a qualified professional.
            </p>
            <p>
              MyDocAI expressly disclaims all liability for decisions made, actions taken, or
              omissions made in reliance on AI-generated output. By using the Service, you accept
              full responsibility for how you interpret and apply any information generated by the
              Service.
            </p>
          </section>

          <section>
            <h2>8. Intellectual Property</h2>
            <p>
              The Service, including its software, user interface, design, text, graphics, logos,
              and all underlying technology, is owned by MyDocAI and is protected by copyright,
              trademark, patent, and other intellectual property laws. Nothing in these Terms
              grants you any right to use MyDocAI's trademarks, logos, or branding without our
              prior written consent.
            </p>
            <p>
              You are granted a limited, revocable, non-exclusive, non-transferable license to
              access and use the Service for its intended purposes in accordance with these Terms.
              This license does not include the right to sublicense, copy, modify, distribute,
              sell, or create derivative works from any part of the Service or its underlying code.
            </p>
            <p>
              If you provide us with feedback, suggestions, or ideas about the Service, you grant
              us the right to use that feedback without restriction or compensation to you.
            </p>
          </section>

          <section>
            <h2>9. Beta Program</h2>
            <p>
              The Service is currently offered as a beta product. This means it is under active
              development, and features may be added, modified, or removed without prior notice.
              You may encounter bugs, errors, or interruptions. By using the Service during the
              beta period, you acknowledge these limitations and agree to provide feedback that
              helps us improve the product.
            </p>
            <p>
              MyDocAI makes no guarantees about the stability, performance, or longevity of the
              Service during the beta period. We are not liable for any losses arising from
              reliance on a beta feature or from changes made to the Service during this period.
            </p>
          </section>

          <section>
            <h2>10. Fees, Billing, and Beta Access</h2>
            <p>
              The Service is currently provided free of charge during the beta period. We reserve
              the right to introduce paid tiers, usage limits, or other pricing structures in the
              future. Before implementing any charges for features that are currently free, we will
              provide registered users with at least 30 days' advance notice via the email address
              associated with their account.
            </p>
            <p>
              If paid subscription features are introduced, billing terms, refund policies, and
              cancellation procedures will be disclosed at the time of purchase. Unless otherwise
              stated, subscription fees are non-refundable except where required by applicable law.
            </p>
          </section>

          <section>
            <h2>11. Privacy</h2>
            <p>
              Your use of the Service is subject to our{' '}
              <Link to="/privacy">Privacy Policy</Link>, which is incorporated into these Terms by
              reference. By using the Service, you consent to the collection and use of your
              information as described in the Privacy Policy. If you do not agree with our privacy
              practices, please do not use the Service.
            </p>
          </section>

          <section>
            <h2>12. Third-Party Services and Links</h2>
            <p>
              The Service integrates with third-party cloud services (including Microsoft Azure and
              SendGrid) to operate. Your use of the Service is also subject to the applicable terms
              and privacy policies of these providers. We are not responsible for the practices of
              any third-party service provider.
            </p>
            <p>
              The Service may contain links to third-party websites or resources. These links are
              provided for your convenience only. We have no control over the content or practices
              of those third-party sites and accept no responsibility for them. Accessing third-party
              links is at your own risk.
            </p>
          </section>

          <section>
            <h2>13. Service Availability and Modifications</h2>
            <p>
              We will use commercially reasonable efforts to keep the Service available and
              operational. However, we do not guarantee uninterrupted, error-free, or continuously
              available access to the Service. We may perform scheduled or emergency maintenance
              at any time, which may result in temporary service interruptions. Where possible,
              we will provide advance notice of planned maintenance windows.
            </p>
            <p>
              We reserve the right to modify, suspend, or discontinue any feature or portion of
              the Service at any time, with or without notice. We are not liable to you or any
              third party for any modification, suspension, or discontinuation of the Service.
            </p>
          </section>

          <section>
            <h2>14. Disclaimer of Warranties</h2>
            <p>
              THE SERVICE IS PROVIDED ON AN "AS IS" AND "AS AVAILABLE" BASIS WITHOUT WARRANTIES
              OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY, OR OTHERWISE. TO THE FULLEST
              EXTENT PERMITTED BY APPLICABLE LAW, MYDOCAI EXPRESSLY DISCLAIMS ALL WARRANTIES,
              INCLUDING BUT NOT LIMITED TO IMPLIED WARRANTIES OF MERCHANTABILITY, FITNESS FOR A
              PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT.
            </p>
            <p>
              We do not warrant that the Service will meet your requirements, that it will be
              uninterrupted, timely, secure, or error-free, that any defects will be corrected,
              or that results obtained from use of the Service will be accurate or reliable.
              You assume full responsibility and risk for your use of the Service.
            </p>
          </section>

          <section>
            <h2>15. Limitation of Liability</h2>
            <p>
              TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT SHALL MYDOCAI,
              ITS OFFICERS, DIRECTORS, EMPLOYEES, AGENTS, OR LICENSORS BE LIABLE FOR ANY INDIRECT,
              INCIDENTAL, SPECIAL, CONSEQUENTIAL, PUNITIVE, OR EXEMPLARY DAMAGES, INCLUDING BUT
              NOT LIMITED TO LOSS OF PROFITS, DATA, GOODWILL, OR OTHER INTANGIBLE LOSSES, ARISING
              OUT OF OR IN CONNECTION WITH YOUR USE OF OR INABILITY TO USE THE SERVICE, EVEN IF
              MYDOCAI HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.
            </p>
            <p>
              IN NO EVENT SHALL MYDOCAI'S TOTAL LIABILITY TO YOU FOR ALL CLAIMS ARISING OUT OF OR
              RELATED TO THESE TERMS OR THE SERVICE EXCEED THE GREATER OF (A) THE AMOUNT YOU PAID
              TO MYDOCAI IN THE TWELVE MONTHS PRECEDING THE CLAIM, OR (B) ONE HUNDRED DOLLARS (USD $100).
            </p>
            <p>
              Some jurisdictions do not allow the exclusion or limitation of incidental or
              consequential damages, so the limitations above may not apply to you in full.
            </p>
          </section>

          <section>
            <h2>16. Indemnification</h2>
            <p>
              You agree to defend, indemnify, and hold harmless MyDocAI and its officers, directors,
              employees, and agents from and against any and all claims, damages, obligations,
              losses, liabilities, costs, and expenses (including reasonable attorneys' fees)
              arising from: (a) your access to or use of the Service; (b) your User Content;
              (c) your violation of these Terms; (d) your violation of any third-party right,
              including any intellectual property right, privacy right, or confidentiality obligation;
              or (e) any claim that your User Content caused damage to a third party.
            </p>
          </section>

          <section>
            <h2>17. Termination</h2>
            <p>
              You may terminate your account at any time by using the account deletion feature
              within the Service or by contacting us at{' '}
              <a href="mailto:support@mydocai.org">support@mydocai.org</a>. Upon termination,
              your right to use the Service ceases immediately, and we will process the deletion
              of your account data in accordance with our Privacy Policy.
            </p>
            <p>
              We reserve the right to suspend or terminate your account and access to the Service
              at any time, with or without notice, if we believe you have violated these Terms,
              engaged in conduct harmful to other users or the Service, or if required by law.
              We will make reasonable efforts to provide advance notice of termination except
              where immediate action is necessary to protect the Service or other users.
            </p>
            <p>
              Provisions of these Terms that by their nature should survive termination shall
              survive, including but not limited to Sections 6 (User Content and License),
              7 (AI-Generated Output), 8 (Intellectual Property), 14 (Disclaimer of Warranties),
              15 (Limitation of Liability), 16 (Indemnification), and 18 (Governing Law).
            </p>
          </section>

          <section>
            <h2>18. Governing Law and Dispute Resolution</h2>
            <p>
              These Terms and any disputes arising out of or relating to them or the Service shall
              be governed by and construed in accordance with the laws of the applicable jurisdiction,
              without regard to conflict of law provisions. You agree to submit to the personal
              jurisdiction of the courts located in the applicable jurisdiction for the resolution
              of any disputes that cannot be resolved informally.
            </p>
            <p>
              Before initiating formal legal proceedings, both parties agree to attempt to resolve
              any dispute through good-faith negotiation. Either party may initiate this process
              by sending written notice of the dispute to the other party. If the dispute is not
              resolved within 30 days of such notice, either party may pursue available legal remedies.
            </p>
          </section>

          <section>
            <h2>19. Changes to These Terms</h2>
            <p>
              We reserve the right to modify these Terms at any time. When we make changes, we will
              update the "Last updated" date at the top of this page. For material changes, we will
              provide additional notice, such as an email notification to the address associated with
              your account or a prominent in-app notice, at least 14 days before the changes take effect.
            </p>
            <p>
              Your continued use of the Service after the effective date of any updated Terms
              constitutes your acceptance of those changes. If you do not agree to the updated
              Terms, you must stop using the Service and may request account deletion.
            </p>
          </section>

          <section>
            <h2>20. General Provisions</h2>
            <p>
              <strong>Entire Agreement.</strong> These Terms, together with the Privacy Policy and
              any other agreements expressly incorporated by reference, constitute the entire
              agreement between you and MyDocAI with respect to the Service and supersede all
              prior agreements and understandings.
            </p>
            <p>
              <strong>Severability.</strong> If any provision of these Terms is found to be
              unenforceable or invalid under applicable law, that provision shall be limited or
              eliminated to the minimum extent necessary so that the remaining provisions remain
              in full force and effect.
            </p>
            <p>
              <strong>No Waiver.</strong> Our failure to enforce any provision of these Terms
              shall not be construed as a waiver of that provision or of our right to enforce it
              in the future.
            </p>
            <p>
              <strong>Assignment.</strong> You may not assign or transfer your rights or obligations
              under these Terms without our prior written consent. We may assign our rights and
              obligations under these Terms without restriction.
            </p>
          </section>

          <section>
            <h2>21. Contact Us</h2>
            <p>
              If you have questions about these Terms, wish to report a violation, or need to
              contact us for any reason related to your use of the Service, please reach out to us at:
            </p>
            <p>
              MyDocAI &nbsp;·&nbsp; <a href="mailto:support@mydocai.org">support@mydocai.org</a>
            </p>
          </section>

        </div>
      </div>
    </div>
  );
};

export default TermsOfServicePage;
